/**
 * Документ поста — PDF внизу поста (docs/release-2026-09-19.md §9.2). Рисуется ПОСЛЕДНИМ
 * блоком пузыря, как его показывает клиент Telegram у расширенного сообщения: круглый значок
 * файла, имя, размер. Порядок — не настройка: блок `document` в `richFromCanonical` всегда
 * последний, и сверка после публикации это проверяет (`document_not_last`).
 *
 * ИМЯ И РАЗМЕР — ИЗ ПОДПИСЫВАЕМОГО ДЕСКРИПТОРА (`canonical.document`), а не из адреса байтов:
 * человек утверждает ровно то имя, которое увидит читатель канала (имя части multipart), и
 * превью обязано показывать его, а не имя, под которым файл лежит у нас.
 *
 * Адрес байтов (`document_ref.media_id`) делает блок ссылкой — скачать утверждаемый файл можно
 * прямо из превью. Нет адреса (сравнение версий без модели, заглушка) — блок остаётся
 * картинкой без действия: имя и размер всё равно правда.
 *
 * Внутри `.tg` фирменных токенов нет (docs/ui-design.md §6): цвета — палитра клиента,
 * литералами в `MessagePreview.module.css`.
 */

import type { DraftContent } from '@smm/contracts';
import type { JSX } from 'react';
import { fileSize } from '../components/format.ts';
import styles from './MessagePreview.module.css';

export interface DocumentBlockProps {
  readonly document: NonNullable<DraftContent['document']>;
  /** Адрес байтов для ссылки «открыть»; `null` — блок без действия. */
  readonly mediaId: string | null;
}

export function DocumentBlock({ document, mediaId }: DocumentBlockProps): JSX.Element {
  const body = (
    <>
      <span className={styles['docIcon']} aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path
            d="M5 2.5h5.2L14 6.3V14a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 4 14V4A1.5 1.5 0 0 1 5.5 2.5Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M10 2.7V6.5h3.8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className={styles['docText']}>
        <span className={styles['docName']}>{document.file_name}</span>
        <span className={styles['docSize']}>{fileSize(document.bytes)}</span>
      </span>
    </>
  );

  if (mediaId === null) {
    return (
      <div className={styles['doc']} data-testid="tg-document">
        {body}
      </div>
    );
  }
  // Скачивание, а не новая вкладка: документ маршрут медиа отдаёт вложением
  // (`Content-Disposition: attachment`), и вкладка только мигнула бы.
  return (
    <a
      className={styles['doc']}
      data-testid="tg-document"
      href={`/api/v1/media/${mediaId}`}
      download={document.file_name}
      title={`Скачать ${document.file_name}`}
    >
      {body}
    </a>
  );
}
